import { useEffect, useState } from 'react'
import type { PortfolioSnapshot } from '@shared/types'
import { api } from '../../lib/api'
import { cn } from '../../lib/utils'
import { performanceSeries } from '@shared/portfolio'
import { dirClass, fmtMoney, fmtPct, fmtPrice } from '../../lib/format'

const RANGES = ['1D', '1W', '1M', '3M', 'ALL'] as const

export function PortfolioChart({ currentValue, currentCost, account = 'all', label = 'Portfolio value' }: { currentValue?: number; /** The capital behind it now (cost of the holdings + cash). */ currentCost?: number; account?: string; label?: string }) {
  const [range, setRange] = useState<string>('1M')
  const [data, setData] = useState<PortfolioSnapshot[] | null>(null)

  useEffect(() => {
    let alive = true
    setData(null)
    api
      .portfolioHistory(range, account)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData([]))
    return () => {
      alive = false
    }
  }, [range, account])

  // Snapshots are recorded hourly; append the LIVE value as the trailing "now" point
  // so the chart and the headline reflect the current (e.g. pre-market) value, not
  // the last stale snapshot.
  const live = typeof currentValue === 'number' && isFinite(currentValue) ? currentValue : null
  const snaps = data ?? []
  const raw = live != null ? [...snaps, { ts: Date.now(), value: live, cost: currentCost ?? snaps[snaps.length - 1]?.cost ?? 0 }] : snaps
  // Deposits and withdrawals are taken out: the line is what the holdings did, ending at today's value.
  const perf = performanceSeries(raw)
  const points = perf.series
  const values = points.map((p) => p.value)
  const min = values.length ? Math.min(...values) : 0
  const max = values.length ? Math.max(...values) : 1
  const span = max - min || 1
  const W = 600
  const H = 160
  const PAD = 4
  // Placed by time, so a quiet night is as wide as it was long.
  const t0 = points[0]?.ts ?? 0
  const tSpan = (points[points.length - 1]?.ts ?? 0) - t0 || 1
  const xy = points.map((p) => {
    const x = points.length > 1 ? ((p.ts - t0) / tSpan) * (W - 2 * PAD) + PAD : W / 2
    const y = H - PAD - ((p.value - min) / span) * (H - 2 * PAD)
    return [x, y] as const
  })
  const line = xy.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')
  const area =
    points.length > 1
      ? `${line} L${xy[xy.length - 1][0].toFixed(1)} ${H - PAD} L${xy[0][0].toFixed(1)} ${H - PAD} Z`
      : ''

  const last = points.length ? points[points.length - 1].value : null
  const headerVal = live ?? last
  const change = perf.gain
  const changePct = perf.gainPct
  const up = change >= 0

  return (
    <div className="card p-3.5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-fg-subtle">{label}</div>
          {headerVal != null && <div className="num text-[18px] font-semibold text-fg">{fmtPrice(headerVal, '$')}</div>}
          {points.length > 1 && (
            <div className={cn('num text-[12px]', dirClass(change))}>
              {change >= 0 ? '+' : ''}
              {fmtPrice(change, '$')} ({fmtPct(changePct)})
              {Math.abs(perf.added) >= 1 && <span className="ml-1.5 text-fg-faint">not counting {fmtMoney(Math.abs(perf.added))} {perf.added > 0 ? 'added' : 'taken out'}</span>}
            </div>
          )}
        </div>
        <div className="inline-flex shrink-0 rounded-lg border border-[var(--line-soft)] p-0.5">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={cn(
                'rounded px-2 py-1 text-[11px] font-medium transition-colors',
                range === r ? 'bg-[var(--color-surface-3)] text-fg' : 'text-fg-subtle hover:text-fg',
              )}
            >
              {r}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3">
        {data === null ? (
          <div className="flex h-[160px] items-center justify-center text-[12px] text-fg-subtle">
            <span className="animate-pulse">Loading…</span>
          </div>
        ) : points.length < 2 ? (
          <div className="flex h-[160px] items-center justify-center px-4 text-center text-[12px] text-fg-faint">
            The value is recorded every 15 minutes while the market is open — the chart fills in from today.
          </div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 160 }} preserveAspectRatio="none">
            <defs>
              <linearGradient id="pf-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={up ? 'var(--color-up)' : 'var(--color-down)'} stopOpacity="0.18" />
                <stop offset="100%" stopColor={up ? 'var(--color-up)' : 'var(--color-down)'} stopOpacity="0" />
              </linearGradient>
            </defs>
            {area && <path d={area} fill="url(#pf-area)" />}
            <path
              d={line}
              fill="none"
              stroke={up ? 'var(--color-up)' : 'var(--color-down)'}
              strokeWidth="1.5"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}
      </div>
    </div>
  )
}
